import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit
} from '@angular/fire/firestore';
import { from, map } from 'rxjs';
import { Model } from '../models/model';

@Injectable({ providedIn: 'root' })
export class ModelService {
  private firestore = inject(Firestore);
  private modelsCol = collection(this.firestore, 'models');

  getModels() {
    return from(getDocs(this.modelsCol)).pipe(
      map(snapshot => snapshot.docs.map(d => Model.fromFirestore(d.id, d.data())))
    );
  }

  getModelById(id: string) {
    const ref = doc(this.firestore, 'models', id);
    return from(getDoc(ref)).pipe(
      map(snap => {
        if (!snap.exists()) throw new Error('Model not found');
        return Model.fromFirestore(snap.id, snap.data());
      })
    );
  }

  /** Optional lookup by user_id if different */
  getModelByUserId(user_id: string) {
    const q = query(this.modelsCol, where('user_id', '==', user_id));
    return from(getDocs(q)).pipe(
      map(snapshot => {
        if (snapshot.empty) return null;
        const d = snapshot.docs[0];
        return Model.fromFirestore(d.id, d.data());
      })
    );
  }

  createModel(model: Model) {
    const id = model.model_id || model.user_id || '';
    if (!id) throw new Error('model_id or user_id required to create model');
    const ref = doc(this.firestore, 'models', id);
    return from(setDoc(ref, model.toFirestore())).pipe(
      map(() => ({ ...model, model_id: id }))
    );
  }

  updateModel(model: Model) {
    if (!model.model_id) throw new Error('model_id required');
    const ref = doc(this.firestore, 'models', model.model_id);
    return from(updateDoc(ref, model.toFirestore())).pipe(map(() => model));
  }

  deleteModel(id: string) {
    const ref = doc(this.firestore, 'models', id);
    return from(deleteDoc(ref)).pipe(map(() => id));
  }

  /** Get newest models ordered by created_at descending */
  getNewestModels(count: number = 8) {
    const q = query(this.modelsCol, orderBy('created_at', 'desc'), limit(count));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(d => Model.fromFirestore(d.id, d.data())))
    );
  }

  /** Get trending models ordered by videosCount descending */
  getTrendingModels(count: number = 6) {
    const q = query(this.modelsCol, orderBy('videosCount', 'desc'), limit(count));
    return from(getDocs(q)).pipe(
      map(snapshot => snapshot.docs.map(d => Model.fromFirestore(d.id, d.data())))
    );
  }
}
